<script setup lang="ts">
  import { ref, onMounted, nextTick } from "vue";
  import { useRoute } from "vue-router";
  import { storeToRefs } from "pinia";
  import { useSurveySimulation } from "@utils/composables/surveySimulation";
  import { useSurveyCardsStore } from "@stores/survey/surveyCards";
  import { useContactsStore } from "@stores/survey/mailing/contacts";
  import TestPreviewToolbar from "./TestPreviewToolbar.vue";
  import type { Survey } from "@/types";

  const props = defineProps<{
    survey: Survey;
  }>();

  const route = useRoute();
  const surveyCardsStore = useSurveyCardsStore();
  const { startFromCardToken } = storeToRefs(surveyCardsStore);
  const { clearStartFromCardToken } = surveyCardsStore;

  const previewMode = ref<"phone" | "desktop">("phone");
  const pParamDraft = ref("");
  const extraParamsDraft = ref("");
  const countryDraft = ref("");
  const contactDraft = ref("");
  const settingsOpen = ref(false);

  const {
    currentCountry,
    currentTestContact,
    isMultiCountry,
    isBrevoTest,
    targetCountryOptions,
    testContactOptions,
    updateCurrentCountry,
    updateTestContact,
    determineCurrentCountry,
    determineCurrentLocale,
    determineCurrentTestContact,
    hasMailingAccess,
    pParam,
    extraParams,
    reloadChat,
    buildTestUrl,
    goToCard,
    chatRef,
  } = useSurveySimulation(props.survey, route.query);

  // Handle initial load: determine defaults then start preview
  onMounted(async () => {
    determineCurrentCountry();
    determineCurrentLocale();
    if (hasMailingAccess.value) {
      await useContactsStore().getContacts();
      determineCurrentTestContact();
    }
    await nextTick();
    if (startFromCardToken.value) {
      const token = startFromCardToken.value;
      clearStartFromCardToken();
      goToCard({ token });
    } else {
      reloadChat();
    }
  });

  const goToTestLink = () => {
    window.open(buildTestUrl(), "_blank");
  };

  const openSettings = () => {
    pParamDraft.value = pParam.value;
    extraParamsDraft.value = extraParams.value;
    countryDraft.value = currentCountry.value;
    contactDraft.value = currentTestContact.value;
    settingsOpen.value = true;
  };

  const applySettings = () => {
    pParam.value = pParamDraft.value;
    extraParams.value = extraParamsDraft.value;
    if (countryDraft.value !== currentCountry.value) {
      updateCurrentCountry(countryDraft.value);
    }
    if (contactDraft.value !== currentTestContact.value) {
      updateTestContact(contactDraft.value);
    }
    settingsOpen.value = false;
    reloadChat();
  };
</script>

<template>
  <div class="flex h-[calc(100vh-100px)] items-center justify-center rounded-lg bg-muted/30 p-4">
    <div :class="['flex items-start h-full gap-4', previewMode === 'desktop' ? 'w-full' : '']">
      <!-- Single iframe with dynamic frame styling -->
      <div
        :class="
          previewMode === 'phone'
            ? 'relative h-full max-h-[900px] aspect-[9/18] bg-white rounded-[2rem] shadow-[0_20px_40px_-15px_rgba(0,0,0,0.12),0_0_0_1px_rgba(0,0,0,0.04)] overflow-hidden'
            : 'w-full h-full max-w-[1200px] bg-white rounded-lg shadow-[0_10px_40px_-10px_rgba(0,0,0,0.1),0_0_0_1px_rgba(0,0,0,0.05)] overflow-hidden'
        ">
        <iframe
          ref="chatRef"
          src="about:blank"
          allow="camera *; microphone *"
          :class="
            previewMode === 'phone'
              ? 'w-[125%] h-[125%] border-none bg-white scale-[0.8] origin-top-left'
              : 'w-full h-full border-none bg-white'
          " />
      </div>

      <TestPreviewToolbar
        v-model:preview-mode="previewMode"
        v-model:settings-open="settingsOpen"
        v-model:p-param-draft="pParamDraft"
        v-model:extra-params-draft="extraParamsDraft"
        v-model:country-draft="countryDraft"
        v-model:contact-draft="contactDraft"
        :is-multi-country="isMultiCountry"
        :is-brevo-test="isBrevoTest"
        :target-country-options="targetCountryOptions"
        :test-contact-options="testContactOptions"
        @reload="reloadChat"
        @open-settings="openSettings"
        @apply-settings="applySettings"
        @open-external="goToTestLink" />
    </div>
  </div>
</template>

